/* eslint-disable @typescript-eslint/naming-convention */
import {
  styled,
  Button,
  FormControl,
  Box,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  colors,
} from '@resiliatech/birdsong'
import { useAppState } from 'Context/AppContext/AppContext'
import { useMutation } from '@apollo/client'
import { FormEvent, useState } from 'react'
import GET_NONPROFIT_ORG_TEAM_QUERY from 'Apollo/Queries/GetNonprofitOrgTeam'
import { team } from 'Modules/Analytics/trackActionInterface/Account/team'
import { analytics } from 'Modules/Analytics'
import REMOVE_NONPROFIT_ORG_USER from 'Apollo/Mutations/RemoveNonprofitOrgUser'
import { useRemoveFunderUserMutation } from 'Generated/graphql-types'
import GET_FUNDER_USERS from 'Apollo/Queries/GetFunderUsers'

type Props = {
  open?: boolean
  setDeletedUser: (user: null) => void
  setShowBirdsongSnackbar: (value: React.SetStateAction<boolean>) => void
  setBirdsongSnackbarMessage: (value: React.SetStateAction<string>) => void
  deletedUser: {
    name?: string
    email: string
    id: number
  } | null
  isFunder?: boolean
}

export const RemoveMemberDialog = ({
  open,
  setDeletedUser,
  deletedUser,
  setShowBirdsongSnackbar,
  setBirdsongSnackbarMessage,
  isFunder = false,
}: Props) => {
  const {
    nonprofitOrgConfig: { platformPlan, orgId, role: nonprofitUserRole },
    userConfig: { email: currentUserEmail, id: currentUserId },
    funderOrgConfig: {
      orgId: funderOrgId,
      user: { role: funderUserRole, funderOrgRole },
    },
  } = useAppState()
  const [removalReason, setRemovalReason] = useState('')
  const [removalExplanation, setRemovalExplanation] = useState('')
  const dispatchRemoveUserSubmitted = analytics.trackActionCreator(
    team.Remove_User_Submitted
  )

  const onDeleteError = () => {
    setShowBirdsongSnackbar(true)
    setBirdsongSnackbarMessage(
      'Unable to remove user, please contact support@resilia.com'
    )
  }

  const sharedOnCompleted = () => {
    setShowBirdsongSnackbar(true)
    setBirdsongSnackbarMessage('Member removed')
    setDeletedUser(null)
    setRemovalExplanation('')
    setRemovalReason('')
  }

  // nonprofit removal
  const [removeUser, { loading }] = useMutation(REMOVE_NONPROFIT_ORG_USER, {
    refetchQueries: [
      {
        query: GET_NONPROFIT_ORG_TEAM_QUERY,
        variables: {
          nonprofitOrgId: orgId,
          planName: platformPlan,
          featureName: 'Team',
        },
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: () => {
      sharedOnCompleted()
      dispatchRemoveUserSubmitted({
        userId: currentUserId,
        email: currentUserEmail,
        nonprofitOrgId: orgId,
        platformPlan,
        funderOrgId,
        removedUser: deletedUser?.email,
        userStatus: 'Deactivated',
        role: nonprofitUserRole,
        userRemovalReason: removalReason,
        userRemovalExplanation: removalExplanation,
      })
    },
    onError: onDeleteError,
  })

  const [removeFunderUser, { loading: funderLoading }] =
    useRemoveFunderUserMutation({
      onCompleted: () => {
        sharedOnCompleted()
        dispatchRemoveUserSubmitted({
          userId: currentUserId,
          email: currentUserEmail,
          platformPlan,
          funderOrgId,
          removedUser: deletedUser?.email,
          userStatus: 'Deactivated',
          role: funderUserRole,
          userRemovalReason: removalReason,
          userRemovalExplanation: removalExplanation,
        })
      },
      refetchQueries: [
        {
          query: GET_FUNDER_USERS,
          variables: {
            funderOrgId,
          },
        },
      ],
      onError: onDeleteError,
    })

  const onSubmitHandler = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isFunder) {
      if (!deletedUser) return
      await removeFunderUser({
        variables: {
          removeFunderUserInfo: {
            userId: deletedUser?.id,
            deletedAt: new Date(),
            currentUserId,
          },
          funderId: funderOrgId,
        },
      })
    } else {
      await removeUser({
        variables: {
          removeNonprofitOrgUserInfo: {
            userId: deletedUser?.id,
            deletedAt: new Date(),
            currentUserId,
          },
          nonprofitOrgId: orgId,
        },
      })
    }
  }

  const handleRadioChange = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const { value } = e.target as HTMLInputElement
    setRemovalReason(removalReason === value ? '' : value)
  }

  const renderFormContent = () => (
    <>
      <StyledTitle variant='h6'>
        Remove {deletedUser?.name || deletedUser?.email} from your organization?
      </StyledTitle>
      <SubTitle>
        Once removed, this team member will no longer be able to login to
        Resilia. All of their work on Resilia will remain with your
        organization.
      </SubTitle>

      <Content>
        <SubTitle style={{ padding: 0 }}>
          Why do you want to remove this team member? (optional)
        </SubTitle>
        <form id='removeMemberDialogForm' onSubmit={onSubmitHandler}>
          <Box style={{ marginLeft: '9px' }}>
            <FormControl
              fullWidth
              margin='normal'
              sx={{ margin: '8px 0 0 0' }}
              required
            >
              <RadioGroup
                aria-label='removal reason'
                name='removalReason'
                value={removalReason}
              >
                <FormControlLabel
                  value='noLongerWithOrg'
                  control={
                    <Radio
                      color='primary'
                      size='small'
                      onClick={(e) => handleRadioChange(e)}
                    />
                  }
                  label='This user is no longer with our organization'
                  key='noLongerWithOrg'
                />
                <FormControlLabel
                  value='roleChange'
                  control={
                    <Radio
                      color='primary'
                      size='small'
                      onClick={(e) => handleRadioChange(e)}
                    />
                  }
                  label='This user changed roles or departments within my organization'
                  key='roleChange'
                />
                <FormControlLabel
                  value='other'
                  control={
                    <Radio
                      color='primary'
                      size='small'
                      onClick={(e) => handleRadioChange(e)}
                    />
                  }
                  label='Other'
                  key='other'
                />
              </RadioGroup>

              <SFormControl
                fullWidth
                visible={removalReason === 'other'}
                sx={{ padding: '8px 40px 0 28px' }}
              >
                <StyledTextfield
                  label='Please explain'
                  variant='outlined'
                  multiline
                  minRows={2}
                  maxRows={2}
                  disabled={removalReason !== 'other'}
                  required={removalReason === 'other'}
                  value={removalExplanation}
                  onChange={(e) => {
                    setRemovalExplanation(e.target.value)
                  }}
                />
              </SFormControl>
            </FormControl>
          </Box>
        </form>
      </Content>

      <DialogActions>
        <Button
          disabled={loading || funderLoading}
          onClick={() => setDeletedUser(null)}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          type='submit'
          form='removeMemberDialogForm'
          disabled={loading || funderLoading}
          color='error'
        >
          {(loading || funderLoading) && (
            <CircularProgress
              size={20}
              style={{
                color: colors.copy.headerLight,
                marginRight: '20px',
              }}
            />
          )}
          {loading || funderLoading ? 'Removing...' : 'Remove Member'}
        </Button>
      </DialogActions>
    </>
  )

  if (!deletedUser) return null

  return (
    <SDialog open={open} onClose={() => setDeletedUser(null)}>
      {renderFormContent()}
    </SDialog>
  )
}

const StyledTitle = styled(DialogTitle)`
  padding: 24px 24px 0 24px;
  margin: 0;
  padding-bottom: 24px;
  margin-right: 24px;
  word-break: break-word;

  @media (max-width: 600px) {
    padding: 12px;
  }
`

const SFormControl = styled(FormControl)<{ visible: boolean }>`
  height: ${({ visible }) => (visible ? '86px' : '0')};
  transition: all 0.3s ease-in-out;
  overflow: hidden;
  margin: 0;
  padding: ${({ visible }) => (visible ? '8px' : '0 8px')};
`

const SDialog = styled(Dialog)`
  & .MuiDialog-paper {
    @media (max-width: 600px) {
      margin: 8px;
    }
  }
`

const SubTitle = styled('p')`
  color: var(--text-primary, rgba(0, 0, 0, 0.87));
  margin: 0;
  padding: 0 24px;
  font-size: 16px;
  line-height: 150%;
  @media (max-width: 600px) {
    width: 100%;
    padding: 0 12px;
  }
`

const Content = styled(DialogContent)`
  padding: 24px;
  width: '600px';
  margin-bottom: 0;

  @media (max-width: 600px) {
    width: 100%;
    padding: 12px;
  }
`

const StyledTextfield = styled(TextField)`
  & .MuiOutlinedInput-root {
    padding: 0;
  }
  & textarea {
    padding: 16px 12px;
  }
`
